import { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import { KeyboardBackspaceRounded } from '@material-ui/icons';

// Components
import ShowHide from 'components/ShowHide/ShowHide';
import { ManualDownloadLink } from 'components/ContinuousImprovement/ManualDownloadLink';
import { Modal } from 'components/ContinuousImprovement/Modal';
import {
  CANCEL_LABEL,
  DuplicateFileDetected,
  PROCEED_LABEL,
} from 'components/ContinuousImprovement/Modal/templates/duplicate-file-detected';
import { PrepareUploadActivitiesModal } from 'components/ContinuousImprovement/Modal/templates/prepare-upload-activities';
import { BatchActivityForm } from 'layouts/pages/activity/forms/batchActivityForm';

// Hooks.
import { useClickEvent, useLoadEvent } from 'hooks';

// Store + Core
import { ACTIVITY_TEMPLATE_ID_MAP, ACTIVITY_TEMPLATE_JA_ID_MAP } from 'store/fileDownload/types';
import { downloadFile } from 'store/fileDownload/actions';
import { isJASelector } from 'store/user/selectors';
import { isModalOpenSelector } from 'store/modal/selectors';
import { closeModal } from 'store/modal/actions';
import { batchActivity, batchActivityClearAction } from 'store/activity/actions';
import { activityFileSelector } from 'store/activity/selectors';
import { taxonomyTabDelimSelector, taxonomyBatchHelpSelector } from 'store/taxonomy/selectors';
import { TAXONOMY_TAB_DELIM_SUPPORT_ID, TAXONOMY_BATCH_HELP_ROOT_ID, TAGS_ENUM } from 'core/constants';
import { getTaxonomyTermById } from 'store/taxonomy/actions';

interface IBatchActivityParams {
  type?: string;
}

const VIEW_UPLOAD_HISTORY_LABEL = 'View Upload History';

const BatchActivityPage = (): ReactElement => {
  const taxonomyTabDelim = useSelector(taxonomyTabDelimSelector);
  const taxonomyBatchHelpTerms = useSelector(taxonomyBatchHelpSelector);
  const { pathname } = useLocation();
  const { type = '' } = useParams<IBatchActivityParams>();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTaxonomyTermById(TAXONOMY_TAB_DELIM_SUPPORT_ID));
    dispatch(getTaxonomyTermById(TAXONOMY_BATCH_HELP_ROOT_ID));
  }, [dispatch]);

  const fireLoadEvent = useLoadEvent({ PageName: 'Activity Batch Upload', PageType: 'Activity Upload' });
  const fireClickEvent = useClickEvent({ Event: 'Activity Upload', EventCategory: 'Activity Upload' });

  useEffect(() => {
    fireLoadEvent();
  }, [fireLoadEvent]);

  // Selectors.
  const isJa: boolean = useSelector(isJASelector);
  const isModalOpen: boolean = useSelector(isModalOpenSelector);
  const file: { file: File } = useSelector(activityFileSelector);

  // Constants.
  const downloadRoute = '/activities/batches/downloadtemplate';
  const matchTemplate: typeof ACTIVITY_TEMPLATE_JA_ID_MAP | typeof ACTIVITY_TEMPLATE_ID_MAP = isJa
    ? ACTIVITY_TEMPLATE_JA_ID_MAP
    : ACTIVITY_TEMPLATE_ID_MAP;

  useEffect(() => {
    if (pathname?.includes(downloadRoute) && matchTemplate[type]) {
      dispatch(downloadFile({ controller: downloadRoute, id: type, matchTemplate }));
    }
  });

  const onProceed = useCallback(() => {
    dispatch(
      batchActivity({
        file: { file } as { file },
        shouldForce: true,
      }),
    );
    fireClickEvent({ EventAction: 'Duplicates', EventLabel: PROCEED_LABEL });
  }, [dispatch, file, fireClickEvent]);

  const onCancel = useCallback(() => {
    dispatch(batchActivityClearAction());
    dispatch(closeModal());
    fireClickEvent({ EventAction: 'Duplicates', EventLabel: CANCEL_LABEL });
  }, [dispatch, fireClickEvent]);

  const jaExcelInstructions = taxonomyBatchHelpTerms?.terms.filter(
    (lt) => lt.tag === TAGS_ENUM.BATCH_HELP__JA_ACTIVITY_EXCEL_INSTRUCTIONS,
  )[0]?.description;
  const parsExcelInstructions = taxonomyBatchHelpTerms?.terms.filter(
    (lt) => lt.tag === TAGS_ENUM.BATCH_HELP__PARS_ACTIVITY_EXCEL_INSTRUCTIONS,
  )[0]?.description;
  const jaXmlInstructions = taxonomyBatchHelpTerms?.terms.filter(
    (lt) => lt.tag === TAGS_ENUM.BATCH_HELP__JA_ACTIVITY_XML_INSTRUCTIONS,
  )[0]?.description;
  const parsXmlInstructions = taxonomyBatchHelpTerms?.terms.filter(
    (lt) => lt.tag === TAGS_ENUM.BATCH_HELP__PARS_ACTIVITY_XML_INSTRUCTIONS,
  )[0]?.description;

  return (
    <section className="form-container">
      <div className="form-title-container">
        <Link
          className="tertiary"
          onClick={() =>
            fireClickEvent({ EventAction: VIEW_UPLOAD_HISTORY_LABEL, EventLabel: '/actvities/batch/history' })
          }
          to="/activities/batch/history"
        >
          {VIEW_UPLOAD_HISTORY_LABEL}
          <KeyboardBackspaceRounded className="tertiary-icon" />
        </Link>
        <h4 className="title">
          <div className="eyebrow">Batch Upload Activities</div>
          Add, update or delete activities
        </h4>
      </div>
      <div className="form-flex-row">
        <div className="form-column">
          <ShowHide title="Help">
            <div className="help-container">
              <h5>The following methods of preparing batch activities are available:</h5>
              <div className="help-list">
                <ul>
                  <li className="help-item help-item--xls">
                    <div className="heading">Excel</div>
                    <p>Upload an Excel file using one of the templates below.</p>
                    <a
                      className="tertiary tertiary--small"
                      href={isJa ? jaExcelInstructions : parsExcelInstructions}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Get the Detailed Excel Instructions
                      <KeyboardBackspaceRounded className="tertiary-icon" />
                    </a>
                    <div className="batch-templates">
                      <ul>
                        <li>
                          <ManualDownloadLink
                            fileName={matchTemplate[isJa ? '4' : '1']}
                            to={downloadRoute}
                            id={isJa ? '4' : '1'}
                            map={matchTemplate}
                          >
                            <>
                              <p>Batch Activity Excel File</p>
                              <p>Use for CME</p>
                            </>
                          </ManualDownloadLink>
                        </li>
                        <li>
                          <ManualDownloadLink
                            fileName={matchTemplate[isJa ? '3' : '0']}
                            to={downloadRoute}
                            id={isJa ? '3' : '0'}
                            map={matchTemplate}
                          >
                            <>
                              <p>Batch Activity with MOC Excel</p>
                              <p>Use for CME and MOC</p>
                            </>
                          </ManualDownloadLink>
                        </li>
                      </ul>
                    </div>
                  </li>
                  {taxonomyTabDelim?.description === 'On' && (
                    <li className="help-item help-item--td">
                      <div className="heading">Tab Delimited</div>
                      <p>Upload a tab delimited file generated from an Excel template.</p>
                      <a
                        className="tertiary tertiary--small"
                        href={
                          isJa
                            ? 'https://www.jointaccreditation.org/sites/default/files/765_20211116_JA_Activity_TabDelimited_Instructions.pdf'
                            : 'https://accme.org/publications/PARS/accme-pars-tab-delimited-instructions-2016-and-greater'
                        }
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Get the Detailed Tab Delimited Instructions
                        <KeyboardBackspaceRounded className="tertiary-icon" />
                      </a>
                      <div className="batch-templates">
                        <ul>
                          <li>
                            <ManualDownloadLink
                              fileName={matchTemplate[isJa ? '5' : '2']}
                              to={downloadRoute}
                              id={isJa ? '5' : '2'}
                              map={matchTemplate}
                            >
                              <>
                                <p>Batch Activity Tab Delimited File</p>
                                <p>Use for CME</p>
                              </>
                            </ManualDownloadLink>
                          </li>
                        </ul>
                      </div>
                    </li>
                  )}
                  <li className="help-item help-item--xml">
                    <div className="heading">XML</div>
                    <p>
                      Upload XML files formatted to conform with the Medical Education Metrics (MEMS) XML created by
                      MedBiquitous Consortium. Technical knowledge of XML is required to use this batch upload feature.
                    </p>
                    <a
                      className="tertiary tertiary--small"
                      href={isJa ? jaXmlInstructions : parsXmlInstructions}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Get the Detailed XML Instructions
                      <KeyboardBackspaceRounded className="tertiary-icon" />
                    </a>
                    <li
                      style={{ color: '#373737', fontSize: '0.75rem', letterSpacing: '0.025rem', lineHeight: 1.66667 }}
                    >
                      Use for CME and MOC
                    </li>
                  </li>
                </ul>
              </div>
            </div>
          </ShowHide>
        </div>
      </div>
      <div className="form-flex-row">
        <div className="form-column grid-4">
          <PrepareUploadActivitiesModal />
          <div className="batch-templates">
            <div className="heading">Batch Templates</div>
            <ul>
              <li>
                <ManualDownloadLink
                  fileName={matchTemplate[isJa ? '4' : '1']}
                  to={downloadRoute}
                  id={isJa ? '4' : '1'}
                  map={matchTemplate}
                >
                  <>
                    <p>Batch Activity Excel File</p>
                    <p>Use for CME</p>
                  </>
                </ManualDownloadLink>
              </li>
              <li>
                <ManualDownloadLink
                  fileName={matchTemplate[isJa ? '3' : '0']}
                  to={downloadRoute}
                  id={isJa ? '3' : '0'}
                  map={matchTemplate}
                >
                  <>
                    <p>Batch Activity with MOC Excel File</p>
                    <p>Use for CME and MOC</p>
                  </>
                </ManualDownloadLink>
              </li>
              {taxonomyTabDelim?.description === 'On' && (
                <li>
                  <ManualDownloadLink
                    fileName={matchTemplate[isJa ? '5' : '2']}
                    to={downloadRoute}
                    id={isJa ? '5' : '2'}
                    map={matchTemplate}
                  >
                    <>
                      <p>Tab Delimited File</p>
                      <p>Use for CME </p>
                    </>
                  </ManualDownloadLink>
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className="form-column">
          <h5>2&#41; Upload your completed file.</h5>
          <BatchActivityForm />
        </div>
        <Modal isOpen={isModalOpen} onClose={() => dispatch(closeModal)} title="Potential duplicate file detected">
          <DuplicateFileDetected onCancel={onCancel} onProceed={onProceed} />
        </Modal>
      </div>
    </section>
  );
};

export default BatchActivityPage;
